// =============================================================================
// DarkModeToggle.tsx
// =============================================================================
// A clean toggle button for switching between light and dark mode.
// Uses the Minimal kit's existing useSettingsContext hook —
// the same mechanism the SettingsDrawer already uses.
//
// Drop anywhere in your app:
//   - Settings page
//   - Topbar / nav
//   - User menu dropdown
//
// Usage:
//   import { DarkModeToggle } from 'src/components/dark-mode-toggle';
//   <DarkModeToggle />
//
// Full-width labeled version for settings pages:
//   <DarkModeToggle variant="labeled" />
// =============================================================================

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  accent:     '#1a4fd6',
  accentDim:  '#dce6fb',
  stone200:   '#e8e6df',
  stone500:   '#7a746c',
  stone800:   '#252320',
  stone900:   '#151412',
} as const;

// ── Props ─────────────────────────────────────────────────────────────────────
type DarkModeToggleProps = {
  /**
   * icon    — small icon button (for topbar / nav)
   * labeled — full row with label and switch (for settings page)
   * switch  — just the switch, no icon (for compact layouts)
   */
  variant?: 'icon' | 'labeled' | 'switch';
};

// ── Component ─────────────────────────────────────────────────────────────────
export function DarkModeToggle({ variant = 'icon' }: DarkModeToggleProps) {
  const { mode, setMode } = useColorScheme();
  const isDark = mode === 'dark';

  const toggle = () => setMode(isDark ? 'light' : 'dark');

  // ── Icon button (topbar / nav) ──────────────────────────────────────────
  if (variant === 'icon') {
    return (
      <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} placement="bottom">
        <IconButton
          onClick={toggle}
          size="small"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            border: `1px solid`,
            borderColor: isDark ? 'rgba(255,255,255,.1)' : T.stone200,
            bgcolor: isDark ? 'rgba(255,255,255,.05)' : 'transparent',
            color: isDark ? 'rgba(255,255,255,.6)' : T.stone500,
            fontSize: '16px',
            transition: 'all .15s ease',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255,255,255,.1)' : T.accentDim,
              borderColor: T.accent,
              color: T.accent,
            },
          }}
        >
          {isDark ? '☀️' : '🌙'}
        </IconButton>
      </Tooltip>
    );
  }

  // ── Switch only ────────────────────────────────────────────────────────
  if (variant === 'switch') {
    return (
      <Switch
        checked={isDark}
        onChange={toggle}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: T.accent },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            bgcolor: T.accent,
          },
        }}
      />
    );
  }

  // ── Labeled row (settings page) ────────────────────────────────────────
  return (
    <Box
      onClick={toggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: '12px',
        border: `1px solid`,
        borderColor: isDark ? 'rgba(255,255,255,.08)' : T.stone200,
        bgcolor: isDark ? 'rgba(255,255,255,.03)' : '#fff',
        cursor: 'pointer',
        transition: 'all .15s ease',
        '&:hover': {
          borderColor: T.accent,
          bgcolor: isDark ? 'rgba(26,79,214,.08)' : T.accentDim,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {/* Icon */}
        <Box sx={{
          width: 36, height: 36,
          borderRadius: '10px',
          bgcolor: isDark ? 'rgba(255,255,255,.07)' : '#f4f3ee',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px',
          transition: 'all .15s',
        }}>
          {isDark ? '☀️' : '🌙'}
        </Box>

        {/* Label */}
        <Box>
          <Typography sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: isDark ? 'rgba(255,255,255,.85)' : T.stone800,
            lineHeight: 1.3,
          }}>
            {isDark ? 'Dark mode' : 'Light mode'}
          </Typography>
          <Typography sx={{
            fontSize: '11px',
            color: isDark ? 'rgba(255,255,255,.35)' : T.stone500,
          }}>
            {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          </Typography>
        </Box>
      </Stack>

      {/* Switch */}
      <Switch
        checked={isDark}
        onChange={toggle}
        onClick={(e) => e.stopPropagation()}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: T.accent },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            bgcolor: T.accent,
            opacity: 0.6,
          },
        }}
      />
    </Box>
  );
}
